/**
 * The signed-in indicator, and the sign-in prompt that stands in for a page when there is
 * no session.
 *
 * THE ROLE BADGE IS NOT AN AUTHORITY CLAIM, and its tooltip says so out loud. The claim
 * was decoded in this browser without a signature check; what a request can actually read
 * is decided by the Postgres role the sidecar switches to. This UI must never suggest
 * that holding "admin" in a badge is what makes a query succeed.
 *
 * THE THREE STAFF TIERS READ THE SAME CURATED VIEWS (POSTGRES_READ_ROLE_FOR maps viewer,
 * editor and admin onto `web_read`), so between them the badge hides nothing in read mode and
 * the claim-driven UI is a convenience on top of a write the database either does or does not
 * permit - the grant form, the session store - never the thing preventing it.
 *
 * THE TWO NON-STAFF TIERS ARE WHERE THE BADGE MEANS SOMETHING ELSE. `player` maps onto
 * `web_player`, reads the `web_self` schema, and every row it can see is filtered by Postgres
 * against the `uuid` claim on the signed token; `unlinked` maps onto no Postgres role at all and
 * reads nothing anywhere. So neither badge is "fewer buttons": each is a different, narrower set of
 * rows, decided in the database. The pages that refuse them (the audit log) say so themselves
 * rather than relying on this badge.
 */
import { useLocation } from 'react-router';

import { useSession } from '../auth/SessionProvider';

const ROLE_EXPLANATION =
  'Decoded from your session token in this browser, without verifying it. ' +
  'It decides what this page offers you; the database decides what you can actually read.';

export function SessionBadge(): React.JSX.Element {
  const { session, signOut } = useSession();
  const location = useLocation();

  // A distinct test id from the panel's button below. Both are on screen at once when
  // signed out, and a shared id makes every `getByTestId('sign-in')` ambiguous.
  if (session === null) return <SignInButton returnTo={location.pathname + location.search} compact testId="sign-in-header" />;

  return (
    <span className="dc-session" data-testid="session-badge">
      <span className="dc-role" data-role={session.claims.role} title={ROLE_EXPLANATION}>
        {session.claims.role}
      </span>
      <span className="dc-session-name">{session.claims.name}</span>
      <button type="button" className="dc-linkish" data-testid="sign-out" onClick={signOut}>
        Sign out
      </button>
    </span>
  );
}

export function SignInButton({
  returnTo,
  compact = false,
  testId = 'sign-in',
}: {
  returnTo: string;
  compact?: boolean;
  testId?: string;
}): React.JSX.Element {
  const { signIn } = useSession();
  return (
    <button type="button" className={compact ? 'dc-linkish' : 'dc-button'} data-testid={testId} onClick={() => signIn(returnTo)}>
      Sign in with Discord
    </button>
  );
}

/**
 * What a page renders instead of its content when there is no session.
 *
 * It issues no queries. That is deliberate beyond tidiness: a signed-out page that fires
 * requests just to watch them 401 pollutes the console, spends rate-limit budget, and
 * makes a real failure harder to spot.
 */
export function SignInPanel(): React.JSX.Element {
  const { authError } = useSession();
  const location = useLocation();

  return (
    <section className="dc-card" data-testid="sign-in-panel">
      <h1>Sign in</h1>
      {authError !== null && (
        <p className="dc-error" role="alert" data-testid="auth-error">
          {authError}
        </p>
      )}
      <p className="dc-dim">
        Read mode is served over the read-only <code>web.*</code> views. Signing in with Discord maps your guild role to a database role whose
        grants are the real limit on what you can see.
      </p>
      <SignInButton returnTo={location.pathname + location.search} />
    </section>
  );
}
